import React, { useState } from 'react';
import * as Yup from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { string } from 'yup';
import {
  getGetCommunityGroupsQueryKey,
  useCreateCommunityGroup,
  useGetCommunityGroups,
} from '@juno/client-api';
import { Site } from '@juno/client-api/model';
import { EMPTY_GROUP_URL } from '@juno/constants';
import { GridV2 } from '@juno/modules';
import {
  Container,
  DefaultSearchSortFilter,
  DialogAriaWrapper,
  GenericDateInput,
  GenericFormikInput,
  TabPanel,
} from '@juno/ui';
import {
  UTCtoLocalString,
  a11yProps,
  slugify,
  usePagesSearchFilter,
  useSettings,
} from '@juno/utils';

const dateError = 'Date is invalid';

interface GroupsAdminProps {
  site?: Site;
}

const validationSchema = Yup.object().shape({
  title: string().required().max(90, 'Title character limit: 90'),
  slug: string().required().max(90, 'Slug character limit: 90'),
  date_released: Yup.date().typeError('Release date must be a valid date').required('Required'),
});

const GroupsAdmin: React.FC<GroupsAdminProps> = ({ site }) => {
  const { enableMentorship } = useSettings();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentTab, setCurrenTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [sort, setSort] = useState('title');
  const [, , searchFilter, setSearch] = usePagesSearchFilter();
  const { data: groups, isFetching: isLoading } = useGetCommunityGroups(site?.id || '', {
    filter: { ...searchFilter, mentor: null },
    order: sort,
  });
  const { data: mentorshipGroups, isFetching: isLoadingMentorshipGroups } = useGetCommunityGroups(
    site?.id || '',
    {
      filter: { ...searchFilter, mentor__isnull: false },
      order: sort,
    },
  );
  const createGroup = useCreateCommunityGroup();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const gridData = groups?.map((group) => ({
    id: group.id,
    title: group.title || '',
    abstract: group.preview_text || '',
    icon: group.list_image || EMPTY_GROUP_URL,
    slug: group.slug || '',
  }));

  const mentorGridData = mentorshipGroups?.map((group) => ({
    id: group.id,
    title: group.title || '',
    abstract: group.preview_text || '',
    icon: group.list_image || EMPTY_GROUP_URL,
    slug: group.slug || '',
  }));

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setCurrenTab(newValue);
  };

  return (
    <Formik
      initialValues={{ title: '', slug: '', date_released: new Date().toISOString() }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          await createGroup.mutateAsync({
            siteId: site?.id || '',
            data: {
              title: values.title,
              slug: values.slug,
              date_released: values.date_released || '',
              tags: [],
            },
          });
          await queryClient.invalidateQueries({
            queryKey: getGetCommunityGroupsQueryKey(site?.id || ''),
          });
          setIsSaving(false);
          setCreateDialogOpen(false);
          navigate(`/${site?.slug}/admin/community/groups/${values.slug}`);
        } catch (e) {
          console.error(e);
          setIsSaving(false);
        }
      }}
      enableReinitialize
    >
      {({
        values,
        errors,
        handleSubmit,
        isValid,
        setFieldValue,
        setFieldTouched,
        resetForm,
        dirty,
        handleChange,
      }) => (
        <Container>
          <Card sx={{ p: 2, mb: 4 }}>
            <Typography variant='h5'>Groups</Typography>
            <Divider sx={{ mt: 2, mb: 4 }} />
            <DefaultSearchSortFilter
              buttonDisabled={false}
              buttonText={'New Group'}
              onClickButton={() => setCreateDialogOpen(true)}
              setSearch={(value: string) => setSearch(value)}
              setSort={setSort}
              sort={sort}
              showFilter={false}
              sortOptions={[
                { value: 'title', label: 'Alphabetical' },
                { value: '-members_count', label: 'Number of Members' },
              ]}
            />
            <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pt: 2 }}>
              <Tab label='Groups' {...a11yProps(0)} />
              {enableMentorship && <Tab label='Mentor Groups' {...a11yProps(1)} />}
            </Tabs>
            <Stack direction='row' flexWrap={'wrap'} gap={2}>
              <TabPanel value={currentTab} index={0}>
                <GridV2 isLoading={isLoading} items={gridData || []} type='group' isAdmin={true} />
              </TabPanel>
              <TabPanel value={currentTab} index={1}>
                <GridV2
                  isLoading={isLoading}
                  items={mentorGridData || []}
                  type='group'
                  isAdmin={true}
                />
              </TabPanel>
              <DialogAriaWrapper
                open={createDialogOpen}
                onClose={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
                id='create_new_page'
              >
                <Box>
                  <Typography variant='h6' sx={{ mt: 3, ml: 3, mb: 2 }}>
                    Create a new group
                  </Typography>
                </Box>
                <DialogContent>
                  <Stack spacing={2}>
                    <GenericFormikInput
                      name='title'
                      label='name'
                      value={values.title}
                      onChange={async (e: any) => {
                        await setFieldValue('title', e.target.value);
                        await setFieldValue('slug', slugify(e.target.value));
                        handleChange(e);
                      }}
                      sx={{ mr: 1 }}
                    />
                    <GenericFormikInput
                      name='slug'
                      label='slug'
                      value={values.slug}
                      sx={{ mr: 1 }}
                    />
                    <GenericDateInput
                      label={
                        <Stack direction='row' alignItems={'center'} gap={1}>
                          *Release Date{' '}
                          <Tooltip title='A release date in the past is required for this group to be visible for users on the platform.'>
                            <InfoOutlinedIcon sx={{ fontSize: 20, mb: 0.25 }} />
                          </Tooltip>
                        </Stack>
                      }
                      name={'date_released'}
                      value={
                        values.date_released
                          ? UTCtoLocalString(new Date(values.date_released || ''))
                          : null
                      }
                      onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                        await setFieldValue(
                          'date_released',
                          !event.target.value ? null : new Date(event.target.value)?.toISOString(),
                        );
                        await setFieldTouched('date_released', true);
                        handleChange(event);
                      }}
                      error={Boolean(errors['date_released'])}
                      helperText={errors['date_released']}
                      sx={{ minWidth: '225px' }}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setCreateDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={isSaving}
                    onClick={() => handleSubmit()}
                    variant='contained'
                    disabled={!isValid}
                  >
                    Create
                  </LoadingButton>
                </DialogActions>
              </DialogAriaWrapper>
            </Stack>
          </Card>
        </Container>
      )}
    </Formik>
  );
};

export default GroupsAdmin;

export const SkeletonPagesTile: React.FC = () => {
  return (
    <Grid item xs={12} md={3} lg={2}>
      <Skeleton variant='rectangular' height={160} width={235} sx={{ borderRadius: 2 }} />
      <Skeleton variant='text' height={20} sx={{ mt: 1, width: '50%' }} />
      <Skeleton variant='text' height={16} sx={{ width: '40%' }} />
    </Grid>
  );
};
